import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { ActorAvatar } from './ActorAvatar';
import AuditChanges from './AuditChanges';
import ActionPill from './ActionPill';
import TechnicianPill from './TechnicianPill';
import { actorLabel, formatTime, isSystemEvent, isTechnicianActor, summary } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';

/** One event, rendered chronologically (J12 timeline) — distinct from the global table's own
 * `EventRow` (`pages/AdminAudit/EventRow.tsx`), which has different props and layout (C2). */
const TimelineItem = ({ event }: { event: AdminAuditEvent }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const deleted = event.actor === null && !isSystemEvent(event);
  const importId = typeof event.context?.importId === 'string' ? event.context.importId : null;
  const hasChanges = Object.keys(event.changes).length > 0;

  return (
    <li className="flex gap-2.5 py-3">
      <ActorAvatar event={event} />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className={cn('text-[13.5px] leading-[1.45]', deleted && 'italic text-muted-foreground')}>
            <span className="font-semibold">{actorLabel(t, event.actor, event.context)}</span>
            {isTechnicianActor(event.actor) && (
              <>
                {' '}
                <TechnicianPill />
              </>
            )}{' '}
            {summary(t, event)}
          </p>
          <span className="shrink-0 font-mono text-[12px] text-muted-foreground">{formatTime(event.occurredAt)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <ActionPill action={event.action} />
          {importId && (
            <Link
              to={`/admin/audit?importId=${importId}`}
              className="text-[12px] text-muted-foreground underline hover:text-foreground"
            >
              {t('View the whole import')}
            </Link>
          )}
          {hasChanges && (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((current) => !current)}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
              {open ? t('Hide') : t('Details')}
            </button>
          )}
        </div>
        {open && hasChanges && <AuditChanges changes={event.changes} />}
      </div>
    </li>
  );
};

export default TimelineItem;
